import {Checkout, PaymentFactory} from "@connox/typescript-library";
import {sentryAddPaymentBreadcrumb} from "../../../helpers/sentryHelper";
import {IPaymentTransaction} from "@connox/typescript-library/types/checkout/requestHandlers/CheckoutRequestHandler";
import braintree, {Client} from "braintree-web";
import {getShopId} from "../getShopId";
import CreditCardModule from "./CreditCardModule";
import DefaultPaymentModule from "./DefaultPaymentModule";
import {IPaymentModule} from "./IPaymentModule";

class PaymentController {
  private braintreeClient: Client | null = null;
  private paymentId?: number;
  private paymentModule: IPaymentModule | null = null;
  private checkout: Checkout | null = null;
  private initPaymentResponse: IPaymentTransaction | null = null;

  public createPayment(checkout: Checkout, paymentId?: number): IPaymentModule | null {
    if (this.paymentModule && this.paymentId === paymentId) {
      return this.paymentModule;
    }

    this.checkout = checkout;
    this.paymentId = paymentId || this.checkout.paymentId || undefined;

    if (!this.paymentId) {
      return null;
    }

    sentryAddPaymentBreadcrumb("createPayment", checkout, {
      modulePaymentId: this.paymentId,
      paramPaymentId: paymentId,
      checkoutPaymentId: checkout.paymentId,
    });
    switch (this.paymentId) {
      case Payments.PaypalBraintree:
        this.paymentModule = new DefaultPaymentModule(checkout);
        break;
      case Payments.CreditCardBraintree:
        this.paymentModule = new CreditCardModule(checkout);
        break;
      default:
        this.paymentModule = new DefaultPaymentModule(checkout);
    }

    return this.paymentModule;
  }

  public getPayment(): IPaymentModule | null {
    return this.paymentModule;
  }

  /**
   * init payment
   */
  public async initPayment(): Promise<IPaymentTransaction> {
    if (!this.checkout) {
      throw new Error("Payment initialization not possible because of missing checkout data");
    }

    this.initPaymentResponse = await PaymentFactory.initPayment(getShopId().toString(), this.checkout.checkoutId);
    sentryAddPaymentBreadcrumb("initPayment", this.checkout, {
      initPaymentResponse: this.initPaymentResponse,
    });
    if (this.initPaymentResponse.state !== "created") {
      throw new Error("initPayment should return the state 'created' but returned: " + this.initPaymentResponse.state);
    }

    return this.initPaymentResponse;
  }

  /**
   * authorize payment
   * @param nonce
   */
  public async authorizePayment(nonce?: string): Promise<IPaymentTransaction> {
    if (!this.checkout) {
      throw new Error("Payment authorization not possible because of missing checkout data");
    }

    let transactionId: string;
    if (!this.initPaymentResponse) {
      if (this.checkout?.paymentId !== Payments.AmazonPay) {
        throw new Error("initPayment has not been called, or received a bad response");
      }
      transactionId = this.checkout.paymentTransaction?.paymentTransactionId ?? "";
    } else {
      transactionId = this.initPaymentResponse.paymentTransactionId;
    }
    if (!transactionId) {
      throw new Error("Amazon Pay initPayment has not been called, or received a bad response");
    }

    const authorizePaymentResponse = await PaymentFactory.authorizePayment(transactionId, nonce);
    sentryAddPaymentBreadcrumb("authorizePayment", this.checkout, {
      authorizePaymentResponse,
    });
    if (authorizePaymentResponse.state !== "authorized") {
      throw new Error(
        "authorizePayment should return the state 'authorized' but returned: " + authorizePaymentResponse.state,
      );
    }
    if (!this.checkout) {
      throw new Error("No checkout object present");
    }

    return authorizePaymentResponse;
  }

  /**
   * init braintree client
   */
  public async initBraintreeClient(): Promise<{client: Client; token: string}> {
    if (!this.checkout?.cartData.currencyCode) {
      throw new Error("Missing currencyCode for BraintreeClient initialisation");
    }

    const {clientToken} = await PaymentFactory.getBraintreeToken(this.checkout?.cartData.currencyCode);
    this.braintreeClient = await braintree.client.create({authorization: clientToken});
    sentryAddPaymentBreadcrumb("initBraintreeClient", this.checkout, {
      braintreeClientCreation: !!this.braintreeClient ? "successful" : "failed",
      cartCurrencyCode: this.checkout.cartData.currencyCode,
    });

    return {client: this.braintreeClient, token: clientToken};
  }

  public getPaymentId(): number | undefined {
    return this.paymentId;
  }

  public isDataComplete(): boolean {
    return !!this.paymentModule?.isDataComplete();
  }
}

const paymentController = new PaymentController();
export {paymentController as PaymentController};

export const isoLocales: {[key: number]: string} = {
  100001: "de_DE",
  100009: "de_AT",
  100017: "de_CH",
  100027: "fr_CH",
  100005: "en_US",
  100020: "en_GB",
  100008: "fr_FR",
  100022: "da_DK",
  100029: "nl_NL",
};

export enum Payments {
  Advance = 100013,
  AmazonPay = 100025,
  CreditCard = 100023,
  CreditCardBraintree = 100031,
  Invoice = 100003,
  Paypal = 100004,
  PaypalBraintree = 100030,
  PaypalExpressBraintree = 100032,
  Paydirekt = 100027,
  SEPA = 100017,
}

// TODO cleanup
export const INTERNAL_ERROR_TYPE = {
  PAYPAL: {
    OnApprove: "PayPalOnApprove",
    OnCreateOrder: "PayPalOnCreateOrder",
    PayPalRelated: "PayPalRelated",
    OnCatch: "PayPalOnCatch",
    OnReEnter: "PayPalCartChange",
    ErrorBoundary: "PayPalErrorBoundary"
  },
  PP_EXPRESS: {
    OnApprove: "PPXOnApprove",
    OnCreateOrder: "PPXOnCreateOrder",
    OnShippingChanged: "PPXOnShippingChanged",
    PayPalRelated: "PPXRelated",
    OnPurchase: "PPXOnPurchaseInSummary",
    OnError: "PPExpressOnErrorCallback",
    OnCatch: "PPExpressOnCatch",
    OnBeforeOpen: "PPExpressBeforeOpen",
  },
  SHIPPING: {
    OnCatch: "Shipping",
  },
  CREDIT_CARD: {
    OnCatch: "CreditCard",
    OnNext: "CreditCardLiability",
  },
  DEFAULT: {
    OnCatch: "DefaultPayment",
  },
};
